import { ConnectionStatus, PeriodicCheckStatus } from '../types';

export interface ConnectionState {
  connectionStatus: ConnectionStatus;
  periodicCheckStatus: PeriodicCheckStatus;
}

export interface ConnectionActions {
  checkConnection: () => Promise<void>;
  startPeriodicConnectionCheck: () => () => void;
}

export const createConnectionModule = (set: any, get: any) => ({
  connectionStatus: {
    isConnected: false,
    lastChecked: null,
    error: null,
    latency: null,
  },

  periodicCheckStatus: {
    isActive: false,
    intervalId: null,
    lastCheck: null,
    checkInterval: 3000,
  },

  checkConnection: async () => {
    const { sdk } = get();
    if (!sdk) {
      set((state: any) => ({
        connectionStatus: {
          ...state.connectionStatus,
          isConnected: false,
          error: 'SDK not initialized',
          lastChecked: new Date(),
        }
      }));
      return;
    }

    const startTime = Date.now();
    try {
      await sdk.setup.isUp();
      const latency = Date.now() - startTime;

      set((_state: any) => ({
        connectionStatus: {
          isConnected: true,
          lastChecked: new Date(),
          error: null,
          latency,
        }
      }));
    } catch (error) {
      // Do not reset sales/transcription state on transient health-check failures.
      // Resetting here causes active-call UI data loss (timer, live summary, ammo).
      set((_state: any) => ({
        connectionStatus: {
          isConnected: false,
          lastChecked: new Date(),
          error: error instanceof Error ? error.message : 'Unknown error',
          latency: null,
        },
      }));
    }
  },

  startPeriodicConnectionCheck: () => {
    const state = get();

    if (state.periodicCheckStatus.isActive && state.periodicCheckStatus.intervalId) {
      return () => { };
    }

    const interval = setInterval(async () => {
      const { connectionStatus } = get();
      const wasConnected = connectionStatus.isConnected;

      set((state: any) => ({
        periodicCheckStatus: {
          ...state.periodicCheckStatus,
          lastCheck: new Date(),
        }
      }));

      // Always check connection status regardless of current state
      await get().checkConnection();

      const isConnectedNow = get().connectionStatus.isConnected;

      // If we just reconnected (was disconnected -> now connected), fetch devices
      if (!wasConnected && isConnectedNow) {
        await get().fetchDevices();
      }
    }, state.periodicCheckStatus.checkInterval);

    set((state: any) => ({
      periodicCheckStatus: {
        ...state.periodicCheckStatus,
        isActive: true,
        intervalId: interval,
      }
    }));

    return () => {
      const currentState = get();
      if (currentState.periodicCheckStatus.intervalId) {
        clearInterval(currentState.periodicCheckStatus.intervalId);
        set((state: any) => ({
          periodicCheckStatus: {
            ...state.periodicCheckStatus,
            isActive: false,
            intervalId: null,
          }
        }));
      }
    };
  },
});
